import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsDataTable, { StatusChip } from "./AnalyticsDataTable";
import api from "../../api";

const LEAD_FIELDS = [
  { key: "worksheet_lead_days", label: "Worksheets" },
  { key: "paper_lead_days", label: "Past Papers" },
  { key: "mock_exam_lead_days", label: "Mock Exams" },
];

const TRIGGER_TARGETS = [
  { value: "all", label: "All (worksheets, papers & mock exams)" },
  { value: "worksheet", label: "Worksheets" },
  { value: "paper", label: "Past Papers" },
  { value: "mock_exam", label: "Mock Exams" },
];

const TYPE_LABELS = {
  worksheet_deadline: "Worksheet deadline",
  paper_deadline: "Past paper deadline",
  mock_exam_deadline: "Mock exam deadline",
  payment_overdue: "Fee overdue",
  payment_link: "Payment link",
  academic_support: "Academic support",
};

const RESOURCE_TYPE_LABELS = {
  course_plan: "Course plan",
  paper: "Past paper",
  mock_exam: "Mock exam",
  installment: "Instalment",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    timeZone: "Europe/London",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const columns = [
  {
    key: "sent_at",
    label: "Sent at",
    render: (row) => fmt(row.sent_at),
  },
  {
    key: "recipient_name",
    label: "Recipient",
    render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {row.recipient_name || "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.recipient_email || ""}
        </Typography>
      </Box>
    ),
  },
  {
    key: "recipient_role",
    label: "Role",
    render: (row) => (
      <Chip
        size="small"
        label={row.recipient_role || "—"}
        sx={{
          fontWeight: 600,
          textTransform: "capitalize",
          bgcolor: row.recipient_role === "parent" ? "#ede9fe" : "#dbeafe",
          color: row.recipient_role === "parent" ? "#7c3aed" : "#2563eb",
        }}
      />
    ),
  },
  {
    key: "type",
    label: "Type",
    render: (row) => TYPE_LABELS[row.type] || row.type || "—",
  },
  {
    key: "resource_name",
    label: "Resource",
    render: (row) => (
      <Box>
        <Typography variant="body2">{row.resource_name || "—"}</Typography>
        {row.student_name && (
          <Typography variant="caption" color="text.secondary">
            Student: {row.student_name}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    key: "deadline_at",
    label: "Deadline",
    render: (row) => fmt(row.deadline_at),
  },
  {
    key: "channels",
    label: "Channels",
    render: (row) =>
      (row.channels || "").split(",").map((c) => (
        <Chip
          key={c}
          size="small"
          label={c.trim()}
          sx={{ mr: 0.5, fontWeight: 600, fontSize: 10 }}
        />
      )),
  },
  {
    key: "status",
    label: "Status",
    render: (row) => <StatusChip status={row.status} />,
  },
];

function SettingsTriggerPanel({ onTriggered }) {
  const [settings, setSettings] = useState({
    worksheet_lead_days: "",
    paper_lead_days: "",
    mock_exam_lead_days: "",
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState("all");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const res = await api.get("admin/analytics/notifications/settings");
      const data = res.data?.data ?? {};
      setSettings({
        worksheet_lead_days: String(data.worksheet_lead_days ?? ""),
        paper_lead_days: String(data.paper_lead_days ?? ""),
        mock_exam_lead_days: String(data.mock_exam_lead_days ?? ""),
      });
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load notification settings");
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleField = (key) => (e) => {
    const v = e.target.value;
    if (v === "" || /^\d{1,2}$/.test(v)) {
      setSettings((s) => ({ ...s, [key]: v }));
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = LEAD_FIELDS.reduce((acc, f) => {
        acc[f.key] = parseInt(settings[f.key], 10) || 0;
        return acc;
      }, {});
      const res = await api.put("admin/analytics/notifications/settings", payload);
      const data = res.data?.data ?? {};
      setSettings({
        worksheet_lead_days: String(data.worksheet_lead_days ?? ""),
        paper_lead_days: String(data.paper_lead_days ?? ""),
        mock_exam_lead_days: String(data.mock_exam_lead_days ?? ""),
      });
      toast.success("Lead times saved");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const runTrigger = async () => {
    setTriggering(true);
    try {
      const res = await api.post("admin/analytics/notifications/trigger", { target });
      toast.success(res.data?.message || "Reminders scan complete");
      setConfirmOpen(false);
      if (onTriggered) onTriggered();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to run reminders");
    } finally {
      setTriggering(false);
    }
  };

  const targetLabel = TRIGGER_TARGETS.find((t) => t.value === target)?.label || target;

  return (
    <Accordion defaultExpanded sx={{ ...CARD_SX, mb: 2, p: 0, "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon fontSize="small" color="action" />
          <Typography fontWeight={700}>Reminder settings &amp; manual trigger</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Lead times (days before deadline)
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {LEAD_FIELDS.map((f) => (
            <Grid item xs={6} sm={3} md={2} key={f.key}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={f.label}
                value={settings[f.key]}
                onChange={handleField(f.key)}
                disabled={loadingSettings}
                inputProps={{ min: 0, max: 60 }}
                helperText="days before"
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={3} md={2}>
            <Button
              variant="contained"
              onClick={saveSettings}
              disabled={saving || loadingSettings}
              sx={{ textTransform: "none" }}
            >
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Run reminder scan now
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Sends due reminders to students &amp; parents with pending assignments. Recipients already
          notified in the last 3 days are skipped automatically.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Target</InputLabel>
              <Select value={target} label="Target" onChange={(e) => setTarget(e.target.value)}>
                {TRIGGER_TARGETS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={triggering}
              sx={{ textTransform: "none" }}
            >
              Send reminders now
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>

      <Dialog open={confirmOpen} onClose={() => !triggering && setConfirmOpen(false)}>
        <DialogTitle>Send reminders now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will scan <strong>{targetLabel}</strong> for upcoming deadlines within the
            configured lead times and send in-app &amp; email reminders to the relevant students and
            parents. Anyone already reminded in the last 3 days is skipped.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={triggering} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={runTrigger}
            variant="contained"
            disabled={triggering}
            startIcon={<SendIcon />}
            sx={{ textTransform: "none" }}
          >
            {triggering ? "Sending…" : "Confirm & send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}

export default function NotificationsAnalytics() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);

  const [typeFilter, setTypeFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: perPage };
      if (typeFilter) params.type = typeFilter;
      if (roleFilter) params.recipient_role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (resourceTypeFilter) params.resource_type = resourceTypeFilter;
      if (from) params.from = from;
      if (to) params.to = to;
      if (search) params.search = search;

      const res = await api.get("admin/analytics/notifications", { params });
      const data = res.data?.data ?? {};
      setRows(data.rows ?? []);
      setTotal(data.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, typeFilter, roleFilter, statusFilter, resourceTypeFilter, from, to, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <AnalyticsPageShell
      title="Notifications Log"
      subtitle="All notification emails and in-app messages sent to students and parents."
      filters={
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Name, email, resource…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} label="Role" onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="parent">Parent</MenuItem>
                <MenuItem value="student">Student</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Resource</InputLabel>
              <Select value={resourceTypeFilter} label="Resource" onChange={(e) => { setResourceTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(RESOURCE_TYPE_LABELS).map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => { setTo(e.target.value); setPage(0); }}
            />
          </Grid>
        </Grid>
      }
    >
      <SettingsTriggerPanel onTriggered={load} />
      <AnalyticsDataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage="No notifications found for the selected filters."
      />
      <Box sx={{ ...CARD_SX, mt: 1, p: 0 }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={perPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Box>
    </AnalyticsPageShell>
  );
}
